import type { DailyReportsScheduleType } from 'api/daily_reports/types'

import { IconButton } from 'components/common'
import { COLUMN_SIZES } from 'components/common/constants'

import { InputFormatWithArrow } from './FormInputWithArrow'
import { MAX_LENGTH } from './constants'
import { DuplicateCSVHeader } from './utils'

import styles from './ScheduleTypeEditor.module.scss'

import type { allHeadersType } from './types'

type ScheduleTypeEditorProps = {
  workspaceId: number
  scheduleType: DailyReportsScheduleType
  allHeaders: allHeadersType
  onChange: (headerIndex: number, newValue: string) => void
  onAddButtonClick: () => void
  onRemoveButtonClick: (headerIndex: number) => void
  onValidChange: (
    workspaceId: number,
    scheduleTypeId: number,
    headerIndex: number,
    isValid: boolean,
    value: string
  ) => void
}

export const ScheduleTypeEditor = (props: ScheduleTypeEditorProps) => {
  const { workspaceId, scheduleType, allHeaders, onChange, onAddButtonClick, onRemoveButtonClick, onValidChange } =
    props

  // ヘッダーが空の場合はデフォルトで空の入力フィールドを表示
  const headers = scheduleType.header.length > 0 ? scheduleType.header : ['']

  // 各 header がバリデーションOKかをチェック
  const isAllHeadersValid = allHeaders
    .flatMap(ws => ws.schedules)
    .filter(s => s.scheduleTypeId === scheduleType.id)
    .every(s => s.isValid && s.value !== '')

  return (
    <>
      {headers.map((value, index) => (
        <div
          key={`${scheduleType.id}-${index}`}
          className="d-flex justify-content-between align-items-center mb-2 gap-2"
        >
          {/* 作業名とアイコンのコンテナ */}
          <div className={`d-flex align-items-center flex-grow-1 flex-shrink-0 ${styles.scheduleTypeNameContainer}`}>
            {index === 0 && (
              <>
                <i
                  className={`font-large ${isAllHeadersValid ? 'icf-check text-success' : 'icf-alert text-warning'}`}
                />
                <div className="ms-2 text-truncate" title={scheduleType.name}>
                  {scheduleType.name}
                </div>
              </>
            )}
          </div>
          {/* テキストボックス（テキストボックスにはラベルを表示しない） */}
          <InputFormatWithArrow
            label=""
            value={value}
            size={COLUMN_SIZES.FULL}
            maxLength={MAX_LENGTH}
            onChange={newValue => onChange(index, newValue)}
            placeholder="文字列を入力"
            validations={[DuplicateCSVHeader(allHeaders, workspaceId, scheduleType.id, index)]}
            maxWidthClassName={styles.scheduleMappingSettingWidth}
            onValidate={isValid => onValidChange(workspaceId, scheduleType.id, index, isValid, value)}
          />
          {/* ボタン用の固定幅コンテナ */}
          <div className={`d-flex align-items-center ${styles.buttonContainer}`}>
            {value.trim() !== '' &&
              index === headers.length - 1 && ( // 入力がある場合のみ追加ボタンを表示
                <IconButton
                  outline
                  icon="plus"
                  size="sm"
                  className="me-1"
                  onClick={onAddButtonClick}
                  tooltipText="追加"
                  name="schedule-header-add-icon-button"
                />
              )}
            {headers.length > 1 && ( // 複数のフィールドがある場合のみ削除ボタンを表示
              <IconButton
                outline
                icon="delete"
                size="sm"
                onClick={() => onRemoveButtonClick(index)}
                tooltipText="削除"
                name="schedule-header-delete-icon-button"
                iconColor="text-danger"
              />
            )}
          </div>
        </div>
      ))}
    </>
  )
}
